import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { HttpErrorHandler, HandleError } from './http-error-handler.service';

import { SessionService } from './session.service';

import { CompanyInformation, FaqTextListItem } from '../models/company';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private _logging = environment.logging;
  private _apiSettings = environment.restAPI;
  private _companyCodeSettings = environment.companyCode;
  private _baseUrl = '';
  private _baseServiceUrl = '';
  private _handleError: HandleError;

  private _companyInformation: CompanyInformation;

  companyInformationSubject = new BehaviorSubject<CompanyInformation>(this.getNewCompanyInformation());
  companyInformation = this.companyInformationSubject.asObservable();

  constructor(
    @Inject('BASE_URL') baseUrl: string,
    private _httpClient: HttpClient,
    private _sessionService: SessionService,
    httpErrorHandler: HttpErrorHandler
  ) {
    this._baseUrl = baseUrl;
    this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
    this._handleError = httpErrorHandler.createHandleError('CompanyService');

    this._companyInformation = this.getNewCompanyInformation();
    this.companyInformation.subscribe(
      response => this._onCompanyInformationChanged(response),
      error => this._handleError('companyInformation.subscribe', error)
    );

    this._refreshCompanyInformation();
  }

  public getNewCompanyInformation(): CompanyInformation {
    const newCompanyInformation: CompanyInformation = {
      companyName: null,
      addressLine1: null,
      addressLine2: null,
      city: null,
      state: null,
      zipCode: null,
      customerServiceNumber: null,
      faxNumber: null,
      customerServiceEmail: null,
      hoursOpen: null,
      hoursClosed: null,
      dayOfWeekOpen: null,
      dayOfWeekClosed: null,
      companyLogoFileName: null,
      backgroundImageFile: null,
      privacyPolicyURL: null,
      termsConditionsURL: null,
      browserRequirementsURL: null,
      militaryBenefitsURL: null,
      loanStatusDescriptionsURL: null,
      consentDisclosureURL: null,
      resourceURL: null
    };
    return newCompanyInformation;
  }

  public getCompanyCode(): string {
    let companyCode: string = this._companyCodeSettings.defaultCompanyCode;

    const useManualCompanyCode: boolean = this._sessionService.getBooleanValueFromSession('useManualCompanyCode');
    const manualCompanyCode: string = this._sessionService.getStringValueFromSession('manualCompanyCode');

    if (useManualCompanyCode !== null && useManualCompanyCode && manualCompanyCode !== null && manualCompanyCode.length > 0) {
      companyCode = manualCompanyCode;
    } else {
      for (const urlLookup of this._companyCodeSettings.urlLookups) {
        if (this._baseUrl.toUpperCase().includes(urlLookup.urlIncludes.toUpperCase())) {
          companyCode = urlLookup.companyCode;
          break;
        }
      }
    }
    return companyCode;
  }

  public setManualCompanyCode(companyCode: string = null) {
    if (companyCode !== null && companyCode.length > 0) {
      this._sessionService.setBooleanValueToSession('useManualCompanyCode', true);
      this._sessionService.setStringValueToSession('manualCompanyCode', companyCode);
      this._refreshCompanyInformation();
    }
  }

  public getFaqList(companyCode: string): Observable<FaqTextListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Company/FAQ';
    if (this._logging.serviceCallTraceLog) {
      console.log('CompanyService.getFaqList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<FaqTextListItem>>(serviceUrl, { companyCode: companyCode }).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getFaqList', []))
    );
  }

  private _refreshCompanyInformation(): void {
    this._getCompanyInformation(this.getCompanyCode()).subscribe(
      result => {
        if (this._logging.serviceCallTraceLog) {
          console.log('CompanyService._refreshCompanyInformation._getCompanyInformation :: result.companyName = ' + result.companyName);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('CompanyService._refreshCompanyInformation._getCompanyInformation :: result = ' + JSON.stringify(result));
        }
        this.companyInformationSubject.next(result);
      },
      error => this._handleError('_refreshCompanyInformation._getCompanyInformation', error)
    );
  }

  private _getCompanyInformation(companyCode: string): Observable<CompanyInformation> {
    const serviceUrl: string = this._baseServiceUrl + 'Company/CompanyInformation';
    if (this._logging.serviceCallTraceLog) {
      console.log('CompanyService._getCompanyInformation :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<CompanyInformation>(serviceUrl, { companyCode: companyCode }).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('_getCompanyInformation', null))
    );
  }

  private _onCompanyInformationChanged(newCompanyInformation: CompanyInformation): void {
    this._companyInformation = newCompanyInformation;
  }
}
